import { defineNuxtPlugin } from '#app'

export enum RecaptchaActions {
  REGISTER = 'REGISTER',
  LOGIN = 'LOGIN',
  LOGIN_2FA = 'LOGIN_2FA',
  CHANGE_2FA = 'CHANGE_2FA',
  CHANGE_PASSWORD = 'CHANGE_PASSWORD',
  CHANGE_EMAIL = 'CHANGE_EMAIL',
  CHANGE_PHONE_NUMBER = 'CHANGE_PHONE_NUMBER',
  RESEND_EMAIL_VALIDATION = 'RESEND_EMAIL_VALIDATION',
  RESEND_SMS_VALIDATION = 'RESEND_SMS_VALIDATION',
  RECOVER_PASSWORD = 'RECOVER_PASSWORD',
}

export default defineNuxtPlugin(async (nuxtApp) => {
  type TRecaptchaApi = typeof window.grecaptcha
  type TEnterpriseRecaptcha = TRecaptchaApi & {
    enterprise?: {
      execute: (
        siteKey: string,
        options: {
          action: string
        },
      ) => Promise<string>
      ready: (callback: () => void) => void
    }
  }

  const { public: config } = useRuntimeConfig()

  const isTestMode = String(config.TEST_MODE).toLowerCase() === 'true'
  if (isTestMode) {
    nuxtApp.provide('recaptcha', {
      async execute(_action = RecaptchaActions.LOGIN) {
        return 'test-token'
      },
    })
    return
  }

  const siteKey = config.NUXT_RECAPTCHA_SITE_KEY
  let grecaptchaInstance: typeof window.grecaptcha | null = null

  const loadRecaptchaScript = (): Promise<typeof window.grecaptcha> =>
    new Promise((resolve, reject) => {
      if (window.grecaptcha) return resolve(window.grecaptcha)

      const script = document.createElement('script')
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`
      script.async = true
      script.defer = true

      script.onload = () =>
        window.grecaptcha
          ? resolve(window.grecaptcha)
          : reject(new Error('grecaptcha unavailable'))

      script.onerror = () => reject(new Error('Failed to load reCAPTCHA'))

      document.head.appendChild(script)
    })

  try {
    grecaptchaInstance = await loadRecaptchaScript()

    nuxtApp.provide('recaptcha', {
      async execute(action = RecaptchaActions.LOGIN) {
        if (!grecaptchaInstance) return

        const captchaApi = (grecaptchaInstance as TEnterpriseRecaptcha)
          .enterprise

        if (!captchaApi) {
          throw new Error('reCAPTCHA enterprise API unavailable')
        }

        return await new Promise<string>((resolve, reject) => {
          captchaApi.ready(() => {
            captchaApi.execute(siteKey, { action }).then(resolve).catch(reject)
          })
        })
      },
    })
  } catch (err) {
    console.error('[reCAPTCHA] init failed:', err)
  }
})
